/**
 * One `/host/my-kanban` fetch per poll tick per agent (ENG-9585).
 *
 * `processAgent` reached for the same board up to three times in a single tick,
 * each a full round trip:
 *
 *   - 7d  board-aware scheduled templates (`hasBoardTemplates`)
 *   - 7e  hybrid kanban-work + scheduled-task reconcile (persistent claude-code)
 *   - 12  the newly-done / waiting-human / stale-card diff — UNCONDITIONAL
 *
 * 7e already reused 7d's result when 7d had run, so the pair was never the
 * duplicate. Section 12 was: it fetched unconditionally, every tick, for every
 * agent, and the fleet is persistent claude-code — which is exactly the shape
 * that guarantees 7e fetched moments earlier. Two identical board payloads per
 * agent per tick, at a 10s default interval.
 *
 * The payload is not small. `/host/my-kanban` is a `select('*')` and only the
 * `done` bucket has its `result` truncated (ENG-4677) — an active card carries
 * its full `result`, `metadata` and `waiting_context`.
 *
 * WHY SUCCESS IS MEMOIZED AND FAILURE IS NOT. Each of the three call sites has
 * its own, different failure policy: 7d and 7e fall back to the cross-tick
 * `kanbanBoardCache` and mark the snapshot stale (the ENG-7557 / ENG-7958 nudge
 * guard depends on that flag); section 12 lets the throw reach its own catch and
 * skips the whole diff, leaving `notifyBoardCache` untouched. Caching a
 * rejection would impose one site's policy on the others — and specifically it
 * would hand section 12 a cached-or-empty board that it would then WRITE to
 * `notifyBoardCache` as this tick's truth. On a first cycle with an empty cache
 * that seeds "nothing is done" over a board that has done cards, and the next
 * tick reports every one of them as newly done: a burst of false completion
 * notifications, to real users, caused by a transport blip.
 *
 * So a rejection is not retained: the next caller re-fetches and gets exactly
 * today's behaviour on that path. The saving is taken on the happy path, which
 * is the one that runs.
 *
 * WHAT THIS COSTS. Section 12 now diffs a snapshot taken earlier in the tick,
 * before sections 8-11 ran. A card that completes mid-tick is therefore reported
 * newly-done on the FOLLOWING tick instead of this one. That is latency, not
 * loss: `notifyBoardCache` is seeded from the same earlier snapshot, so the next
 * tick's diff still sees the transition. The bound is one poll interval — 10s by
 * default (`agt manager start --interval`, floor 5s). The stale-card reaper is
 * unaffected: it ages `updated_at` against `Date.now()`, so a card that has just
 * moved has a fresh timestamp and cannot be reaped by an older snapshot.
 */

/**
 * Returns a function that performs `fetchBoard` at most once per successful
 * result, sharing that result with every later caller in the same tick.
 *
 * Create one per agent per tick, and let it fall out of scope with the tick —
 * it holds a board snapshot, so a longer-lived instance would serve stale cards.
 * The cross-tick cache is `kanbanBoardCache`, which is a different thing with a
 * different purpose (a fallback for a FAILED fetch, deliberately stale).
 *
 * Concurrent callers share one in-flight request rather than racing two. The
 * three call sites are sequential `await`s today, so this is insurance against a
 * future one, not a live requirement.
 */
export function createTickBoardFetcher<T>(fetchBoard: () => Promise<T>): () => Promise<T> {
  let resolved: { value: T } | null = null;
  let inflight: Promise<T> | null = null;

  return () => {
    if (resolved) return Promise.resolve(resolved.value);
    if (inflight) return inflight;

    inflight = fetchBoard().then(
      (value) => {
        resolved = { value };
        inflight = null;
        return value;
      },
      (err) => {
        // Deliberately NOT retained — see the header. The next caller retries.
        inflight = null;
        throw err;
      },
    );
    return inflight;
  };
}
